import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Center,
  Container,
  Grid,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
  useMantineColorScheme,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconEdit, IconCheck, IconX, IconArrowLeft } from "@tabler/icons-react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useFranchiseDetailsQuery,
  useFranchiseTeamsQuery,
  useUpdateFranchiseMutation,
  useSetFranchiseGmMutation,
  useSetFranchiseAgmMutation,
  useUserSearchLazyQuery,
} from "generated/graphql";

/**
 * FranchiseEdit
 *
 * Admin page for editing franchise details.
 * Uses FranchiseDetails query to load data and various mutations to save.
 */
export default function FranchiseEdit() {
  const { franchiseId } = useParams();
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  // Fetch franchise details
  const { data, loading, error, refetch } = useFranchiseDetailsQuery({
    variables: { franchiseId: franchiseId! },
    skip: !franchiseId,
    fetchPolicy: "cache-and-network",
  });

  // Fetch franchise teams
  const { data: teamsData, loading: teamsLoading } = useFranchiseTeamsQuery({
    variables: { franchiseId: franchiseId! },
    skip: !franchiseId,
    fetchPolicy: "cache-and-network",
  });

  // Mutations
  const [updateFranchise, { loading: savingFranchise }] = useUpdateFranchiseMutation();
  const [setFranchiseGM, { loading: savingGM }] = useSetFranchiseGmMutation();
  const [setFranchiseAGM, { loading: savingAGM }] = useSetFranchiseAgmMutation();

  // User search for GM/AGM dropdowns
  const [searchUsers, { data: userSearchData, loading: searchingUsers }] = useUserSearchLazyQuery();

  // Editable state
  const [name, setName] = useState<string>("");
  const [active, setActive] = useState<boolean>(true);
  const [gmId, setGmId] = useState<string | null>(null);
  const [agmId, setAgmId] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Inline name editing state
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");

  // User search state
  const [userSearchTerm, setUserSearchTerm] = useState("");

  // Populate form when data loads
  useEffect(() => {
    if (data?.franchise) {
      setName(data.franchise.name);
      setActive(data.franchise.active);
      setGmId(data.franchise.GM?.id ?? null);
      setAgmId(data.franchise.AGM?.id ?? null);
      setNameDraft(data.franchise.name);
      setHasChanges(false);
    }
  }, [data]);

  // Track changes
  useEffect(() => {
    if (data?.franchise) {
      const nameChanged = name !== data.franchise.name;
      const activeChanged = active !== data.franchise.active;
      const gmChanged = gmId !== (data.franchise.GM?.id ?? null);
      const agmChanged = agmId !== (data.franchise.AGM?.id ?? null);
      setHasChanges(nameChanged || activeChanged || gmChanged || agmChanged);
    }
  }, [name, active, gmId, agmId, data]);

  // Search users when user types in the dropdown
  useEffect(() => {
    if (userSearchTerm.length >= 2) {
      searchUsers({ variables: { input: userSearchTerm } });
    }
  }, [userSearchTerm, searchUsers]);

  // Build user options for dropdowns, including current GM/AGM
  const userOptions = useMemo(() => {
    const options: { value: string; label: string }[] = [];

    // Add current GM if exists
    if (data?.franchise?.GM) {
      options.push({ value: data.franchise.GM.id, label: data.franchise.GM.name });
    }

    // Add current AGM if exists and not same as GM
    if (data?.franchise?.AGM && data.franchise.AGM.id !== data.franchise.GM?.id) {
      options.push({ value: data.franchise.AGM.id, label: data.franchise.AGM.name });
    }

    // Add search results
    if (userSearchData?.users?.items) {
      for (const user of userSearchData.users.items) {
        if (!options.some((o) => o.value === user.id)) {
          options.push({ value: user.id, label: user.name });
        }
      }
    }

    return options;
  }, [data, userSearchData]);

  async function handleSave() {
    if (!franchiseId || !name.trim()) return;

    try {
      const nameChanged = name !== data?.franchise?.name;
      const activeChanged = active !== data?.franchise?.active;
      const gmChanged = gmId !== (data?.franchise?.GM?.id ?? null);
      const agmChanged = agmId !== (data?.franchise?.AGM?.id ?? null);

      // Update name/active if changed
      if (nameChanged || activeChanged) {
        const result = await updateFranchise({
          variables: {
            franchiseId: franchiseId,
            name: nameChanged ? name.trim() : undefined,
            active: activeChanged ? active : undefined,
          },
        });

        if (!result.data?.updateFranchise) {
          notifications.show({
            title: "Error",
            message: "Failed to update franchise",
            color: "red",
          });
          return;
        }
      }

      // Update GM if changed
      if (gmChanged && gmId) {
        const result = await setFranchiseGM({
          variables: {
            franchiseId: franchiseId,
            userId: gmId,
          },
        });

        if (!result.data?.setFranchiseGM?.successful) {
          notifications.show({
            title: "Error",
            message: "Failed to update GM",
            color: "red",
          });
          return;
        }
      }

      // Update AGM if changed
      if (agmChanged && agmId) {
        const result = await setFranchiseAGM({
          variables: {
            franchiseId: franchiseId,
            userId: agmId,
          },
        });

        if (!result.data?.setFranchiseAGM?.successful) {
          notifications.show({
            title: "Error",
            message: "Failed to update AGM",
            color: "red",
          });
          return;
        }
      }

      notifications.show({
        title: "Success",
        message: "Franchise updated successfully",
        color: "green",
      });
      navigate("/admin-hub/franchises");
    } catch (err) {
      notifications.show({
        title: "Error",
        message: "An error occurred while updating the franchise",
        color: "red",
      });
    }
  }

  const saving = savingFranchise || savingGM || savingAGM;

  if (loading && !data) {
    return (
      <Center style={{ height: "calc(100dvh - 48px)" }}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (error || !data?.franchise) {
    return (
      <Container size="xl" px="lg" py="lg">
        <Group justify="flex-start" mb="sm">
          <Button
            variant="filled"
            color="blue"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate("/admin-hub/franchises")}
          >
            Back to Franchises
          </Button>
        </Group>
        <Text c="red">Failed to load franchise data.</Text>
      </Container>
    );
  }

  const franchise = data.franchise;
  const teams = teamsData?.teams?.items ?? [];

  return (
    <Container size="xl" px="lg" py="lg">
      <Stack gap="md">
        <Group justify="flex-start">
          <Button
            variant="filled"
            color="blue"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate("/admin-hub/franchises")}
          >
            Back to Franchises
          </Button>
        </Group>

        {/* Header: Title (editable), ID, Status badge and Active toggle */}
        <Group justify="space-between" align="center">
          <div>
            <Group gap="xs" align="center">
              {editingName ? (
                <>
                  <TextInput
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.currentTarget.value)}
                    size="md"
                  />
                  <Button
                    size="xs"
                    variant="subtle"
                    onClick={() => {
                      setName(nameDraft);
                      setEditingName(false);
                    }}
                    leftSection={<IconCheck size={14} />}
                  >
                    Save
                  </Button>
                  <Button
                    size="xs"
                    variant="subtle"
                    onClick={() => {
                      setNameDraft(name);
                      setEditingName(false);
                    }}
                    leftSection={<IconX size={14} />}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  <Title c={isDark ? "white" : "black"}>{name}</Title>
                  <Button
                    size="xs"
                    variant="subtle"
                    leftSection={<IconEdit size={14} />}
                    onClick={() => setEditingName(true)}
                  >
                    Edit
                  </Button>
                </>
              )}
            </Group>
            <Text size="sm" c={isDark ? "gray.4" : "gray.7"}>
              Franchise ID: {franchiseId}
            </Text>
            {franchise.prefix && (
              <Text size="sm" c={isDark ? "gray.4" : "gray.7"}>
                Prefix: {franchise.prefix}
              </Text>
            )}
          </div>
          <Group gap="md" align="center">
            <Badge color={active ? "green" : "red"} variant="light">
              {active ? "Active" : "Disabled"}
            </Badge>
            <Switch
              checked={active}
              onChange={(e) => setActive(e.currentTarget.checked)}
              label="Active"
            />
          </Group>
        </Group>

        {/* Leadership (GM/AGM) */}
        <Paper withBorder radius="md" p="lg" shadow="sm">
          <Stack gap="md">
            <Text size="sm" c={isDark ? "gray.4" : "gray.7"}>
              Assign franchise leadership
            </Text>
            <Grid gutter="md">
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Select
                  label="General Manager"
                  placeholder="Search for a user..."
                  data={userOptions}
                  value={gmId}
                  onChange={setGmId}
                  searchable
                  onSearchChange={setUserSearchTerm}
                  nothingFoundMessage={searchingUsers ? "Searching..." : "No matches"}
                  clearable
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Select
                  label="Assistant GM"
                  placeholder="Search for a user..."
                  data={userOptions}
                  value={agmId}
                  onChange={setAgmId}
                  searchable
                  onSearchChange={setUserSearchTerm}
                  nothingFoundMessage={searchingUsers ? "Searching..." : "No matches"}
                  clearable
                />
              </Grid.Col>
            </Grid>
          </Stack>
        </Paper>

        {/* Teams in this franchise (read-only) */}
        <Paper withBorder radius="md" p="lg" shadow="sm">
          <Group justify="space-between" align="center" mb="sm">
            <Text fw={600}>Teams in this franchise</Text>
            <Text size="sm" c={isDark ? "gray.5" : "gray.6"}>
              {teams.length} team{teams.length !== 1 ? "s" : ""}
            </Text>
          </Group>
          {teamsLoading ? (
            <Center py="md">
              <Loader size="sm" />
            </Center>
          ) : (
            <Table
              striped
              highlightOnHover
              withTableBorder
              withColumnBorders
              horizontalSpacing="lg"
              verticalSpacing="sm"
            >
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Team</Table.Th>
                  <Table.Th>Players</Table.Th>
                  <Table.Th>Status</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {teams.map((t) => (
                  <Table.Tr key={t.id}>
                    <Table.Td>{t.name}</Table.Td>
                    <Table.Td>{t.players?.length ?? 0}</Table.Td>
                    <Table.Td>
                      {t.active ? (
                        <Badge color="green" variant="light">
                          Active
                        </Badge>
                      ) : (
                        <Badge color="red" variant="light">
                          Disabled
                        </Badge>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
                {teams.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={3}>
                      <Text size="sm" c={isDark ? "gray.5" : "gray.6"}>
                        No teams in this franchise.
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          )}
        </Paper>

        {/* Bottom actions */}
        <Group justify="flex-end" mt="md">
          <Button variant="light" color="gray" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button
            className="bg-blue-500 hover:bg-blue-600"
            onClick={handleSave}
            disabled={!hasChanges || !name.trim() || saving}
            loading={saving}
          >
            Save Changes
          </Button>
        </Group>
      </Stack>
    </Container>
  );
}
